import { useCallback, useEffect, useRef, useState } from 'react';

import { useAtomsToastManager } from '@opendoor/atoms/toast';

import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { buildRenameInput } from './mapping';
import { useSavedSearches } from './useSavedSearches';

/**
 * State and actions behind a saved-search list: the signed-in list, inline
 * rename, and the delete confirmation, each confirmed with a toast.
 */
export const useSavedSearchesMenu = () => {
  const toastManager = useAtomsToastManager();
  // `useAtomsToastManager` returns a fresh object each render, so read it
  // through a ref instead of a dependency.
  const toastManagerRef = useRef(toastManager);
  useEffect(() => {
    toastManagerRef.current = toastManager;
  });
  // A list in a panel mounts each time the panel opens; show the cached list
  // instead of requesting it again on every open.
  const { isLoggedIn, savedSearches, update, remove } = useSavedSearches({
    refetchOnMount: false,
  });
  const [pendingDelete, setPendingDelete] = useState<SavedSearchFieldsFragment | null>(null);

  const rename = useCallback(
    async (savedSearch: SavedSearchFieldsFragment, newName: string) => {
      const result = await update(savedSearch.id, buildRenameInput(savedSearch, newName));
      if (result.success) {
        toastManagerRef.current.add({ title: `Renamed to “${newName}”`, variant: 'success' });
      }
      return result;
    },
    [update],
  );

  const confirmDelete = useCallback(
    async (id: string) => {
      const result = await remove(id);
      if (result.success) {
        toastManagerRef.current.add({ title: 'Saved search deleted', variant: 'success' });
      }
      return result;
    },
    [remove],
  );

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  return {
    isLoggedIn,
    savedSearches,
    pendingDelete,
    requestDelete: setPendingDelete,
    cancelDelete,
    rename,
    confirmDelete,
  };
};
